import type { TrustPilotBusinessType } from "@/api/reviewTrustPilotApi";
import type { CouponType } from "@/types/trustpilotPricing";
import { FUNNEL_TRUST_PILOT } from "@/types/funnelTypes";
import { calculateOrderPrice } from "@/utils/orderHelper";

const ORDER_RESULT_SUMMARY_KEY = "reputech:order-result-summary";

export type PlatformOrderResultSummary = {
  companyName: string;
  improvement: number;
  projectedRating: number;
  reviewCount: number;
  totalPrice: string;
};

const getProjectedRating = (company: TrustPilotBusinessType) => {
  const rating = Number(company.rating) || 0;
  const reviewCount = Number(company.review_count) || 0;
  const selectedReviews = company.userSelectedReviews ?? [];
  const remainingReviews = reviewCount - selectedReviews.length;
  if (!rating || reviewCount <= 0 || remainingReviews <= 0) return rating;

  const removedRatingTotal = selectedReviews.reduce(
    (total, review) => total + Number(review.rating || 0),
    0,
  );
  return Math.max(
    0,
    Math.min(5, Math.round(((rating * reviewCount - removedRatingTotal) / remainingReviews) * 10) / 10),
  );
};

export const getPlatformOrderResultSummaryKey = (
  _funnelType: typeof FUNNEL_TRUST_PILOT = FUNNEL_TRUST_PILOT,
) => ORDER_RESULT_SUMMARY_KEY;

export function createPlatformOrderResultSummary(
  _funnelType: typeof FUNNEL_TRUST_PILOT,
  companies: TrustPilotBusinessType[],
  pricePerReview: number,
  coupon: CouponType | null,
  checkoutTotalPrice?: string,
): PlatformOrderResultSummary | null {
  const company = companies.find((item) => (item.userSelectedReviews?.length ?? 0) > 0);
  if (!company) return null;

  const reviewCount = company.userSelectedReviews?.length ?? 0;
  const projectedRating = getProjectedRating(company);
  const rating = Number(company.rating) || projectedRating;

  return {
    companyName: company.name?.trim() || "Your business",
    improvement: Math.max(0, Math.round((projectedRating - rating) * 10) / 10),
    projectedRating,
    reviewCount,
    totalPrice: checkoutTotalPrice ?? String(calculateOrderPrice(coupon, pricePerReview, reviewCount)),
  };
}

export function persistPlatformOrderResultSummary(
  _funnelType: typeof FUNNEL_TRUST_PILOT,
  summary: PlatformOrderResultSummary | null,
) {
  if (!summary || typeof window === "undefined") return;
  window.sessionStorage.setItem(ORDER_RESULT_SUMMARY_KEY, JSON.stringify(summary));
}
